/**
 * Conflict screens (damage assessments, FEMA costs, ICS-214 unit logs) through the originator
 * gate (client-id.ts mustDeferSend), the same decision every sync path uses:
 *  - the list shows only conflicts this session may resolve;
 *  - a resolution acts only on a row that is still this session's when it runs. "Keep local"
 *    would send another originator's change as this user; "keep server" would overwrite their
 *    pending change, and it would then never be handed off.
 */
import type { Table } from 'dexie';
import { mustDeferSend } from './client-id';
import type { OriginatorStamp } from './db';

type Stamped = OriginatorStamp & { id: string; tenant_id?: unknown };

/** The conflicts this session may resolve. */
export function resolvableConflicts<T extends Stamped>(rows: readonly T[]): T[] {
  return rows.filter((r) => !mustDeferSend(r));
}

/**
 * The row as stored now, when this session may resolve its conflict; null when it may not
 * (another originator's or tenant's, an unknown originator's, or no session/tenant). Read
 * fresh: the list on screen may be stale.
 */
export async function ownConflictOrNull<T extends Stamped>(table: Table<T, string>, row: T): Promise<T | null> {
  const current = (await table.get(row.id)) ?? row;
  return mustDeferSend(current) ? null : current;
}
